import React from 'react';
import { EyeIcon, LockIcon, MailIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { InitialAvatar } from '../InitialAvatar';
import { PhotoSide } from './PhotoSide';
import { PANEL_PHOTOS, RECENT_ARRIVALS, decadeFor, membersFromDecade } from '../../data/registration';
import type { RegistrationDraft } from '../../types/registration';
interface RegistrationCompleteProps {
  draft: RegistrationDraft;
  onStartOver: () => void;
  onDone?: () => void;
}
function publicName(draft: RegistrationDraft): string {
  const first = draft.firstName.trim() || 'You';
  const initial = draft.lastName.trim().charAt(0);
  return initial ? `${first} ${initial}.` : first;
}
function yearsLabel(draft: RegistrationDraft): string {
  if (!draft.startYear) return 'Years not set';
  if (draft.stillInvolved) return `${draft.startYear}–present`;
  if (!draft.endYear || draft.endYear === draft.startYear) return draft.startYear;
  return `${draft.startYear}–${draft.endYear}`;
}
export function RegistrationComplete({
  draft,
  onStartOver,
  onDone
}: RegistrationCompleteProps) {
  const decade = decadeFor(draft.startYear || '1990');
  const cohort = membersFromDecade(draft.startYear || '1990');
  const isPublic = draft.visibility === 'public';
  return <div className="flex min-h-screen w-full flex-col bg-canvas lg:grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)]" data-id="element-475" data-mp="l0sdryzxupvs">
      <div className="h-[38vh] min-h-56 w-full lg:sticky lg:top-0 lg:h-screen" data-id="element-476" data-mp="c26shw330wrb">
        <PhotoSide image={PANEL_PHOTOS.voices} caption={{
        eyebrow: `The ${decade}`,
        line: `You’re one of ${cohort + 1} people here from the ${decade}.`,
        names: RECENT_ARRIVALS[decade] ?? []
      }} data-id="element-477" data-mp="g3jwe0mm6pvl" />
      </div>

      <div className="flex flex-1 flex-col justify-center px-6 py-12 sm:px-10 lg:px-16" data-id="element-478" data-mp="74y5ni6zo4jq">
        <div className="flex w-full max-w-xl flex-col gap-7" data-id="element-479" data-mp="1wil3v639ojvt">
          <div className="flex flex-col gap-3" data-id="element-480" data-mp="21h4pt6hv1vo6">
            <span className="text-eyebrow-caption font-semibold uppercase text-ink-muted" data-id="element-481" data-mp="21esal13hlzet">
              You&apos;re in
            </span>
            <h1 className="text-page-title font-semibold text-ink sm:text-display" data-id="element-482" data-mp="1v037juh8gbr0">
              Welcome back, {draft.firstName.trim() || 'friend'}.
            </h1>
            <p className="text-body text-ink-muted" data-id="element-483" data-mp="1q3ctfcoznq88">
              Check <span className="text-ink" data-id="element-484" data-mp="1l3qft25907yw">{draft.email.trim() || 'your inbox'}</span> for a
              link to set your password. It comes from the portal, not a marketing address — and
              you can resend it from the sign-in page if it doesn&apos;t arrive.
            </p>
          </div>

          <div className="rounded-card border border-rule bg-card" data-id="element-485" data-mp="28z2xd5ghewor">
            <div className="flex items-center gap-4 border-b border-rule p-5" data-id="element-486" data-mp="241ugd872kyp3">
              <InitialAvatar name={`${draft.firstName} ${draft.lastName}`} size={52} data-id="element-487" data-mp="31goujftd5uqp" />
              <div className="flex flex-col gap-0.5" data-id="element-488" data-mp="31ijg8wcglqvu">
                <span className="text-heading font-semibold text-ink" data-id="element-489" data-mp="2wp6qujzyhg21">{publicName(draft)}</span>
                <span className="text-small text-ink-muted" data-id="element-490" data-mp="34zntnk71ogv3">
                  {yearsLabel(draft)} ·{' '}
                  {isPublic ? 'findable by members' : 'visible only to you'}
                </span>
              </div>
            </div>

            <dl className="flex flex-col gap-4 p-5 text-small" data-id="element-491" data-mp="3e1neyop3e73u">
              <div className="flex items-start gap-3" data-id="element-492" data-mp="3iz6v5u5cotyf">
                {isPublic ? <EyeIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-primary-text" data-id="element-493" data-mp="3nwf8nhfk09tc" /> : <LockIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-primary-text" data-id="element-494" data-mp="3rx90un014as1" />}
                <div data-id="element-495" data-mp="mqsmn6yildr">
                  <dt className="font-medium text-ink" data-id="element-496" data-mp="5j7ns1ckq00c">
                    {isPublic ? 'Members from your years can find you' : 'Nobody can see you yet'}
                  </dt>
                  <dd className="text-ink-muted" data-id="element-497" data-mp="agr4d7wquovg">
                    First name and last initial only. One tap to change, whenever you like.
                  </dd>
                </div>
              </div>

              <div className="flex items-start gap-3" data-id="element-498" data-mp="179yw7t139rj4">
                <MailIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-primary-text" data-id="element-499" data-mp="179frje0g8zn1" />
                <div data-id="element-500" data-mp="1lybwv3b4fyjy">
                  <dt className="font-medium text-ink" data-id="element-501" data-mp="1m0y0iruw5tww">
                    {draft.memory.trim() ? draft.shareMemory ? 'Your memory is with a staff member' : 'Your memory is saved privately' : 'Your contact details stay with the portal team'}
                  </dt>
                  <dd className="text-ink-muted" data-id="element-502" data-mp="12bxd583nxs7n">
                    {draft.memory.trim() ? 'It joins the archive only after someone has read it, and you can withdraw it.' : 'Never shown to another member, never used to identify you.'}
                  </dd>
                </div>
              </div>
            </dl>
          </div>

          <div className="flex flex-wrap items-center gap-3" data-id="element-503" data-mp="2h7fj3e3y5lt9">
            <Button size="lg" onClick={onDone} data-id="element-504" data-mp="2ha1pvbj0jxaf">
              See who else is here
            </Button>
            <Button variant="quiet" onClick={onStartOver} data-id="element-505" data-mp="2m4p48hkazbba">
              Walk through it again
            </Button>
          </div>
        </div>
      </div>
    </div>;
}